import { RefObject, useEffect } from 'react';

import type { ShadowContainerProps } from 'components/listTablePage';

import { Shadow } from './Shadow';
import { ShadowContainer } from './ShadowContainer';
import type { StickyElementRefs } from './hooks';
import { useOverflowBox, useStickyContentBox } from './hooks';

export type DefaultTableShadowOverlayProps = Omit<
  ShadowContainerProps,
  'overflowBox' | 'stickyContentBox' | 'children'
> & {
  overflowBoxRef: RefObject<HTMLElement>;
  stickyElementRefs: StickyElementRefs;
  enableStickyBorders?: boolean;
};

export const DefaultTableShadowOverlay = ({
  overflowBoxRef,
  stickyElementRefs,
  depth = 16,
  enableStickyBorders,
  ...restProps
}: DefaultTableShadowOverlayProps) => {
  const { updateOverflowBox, overflowBox } = useOverflowBox({ overflowBoxRef });

  const { stickyContentBox } = useStickyContentBox({
    overflowBoxRef,
    stickyElementRefs,
  });

  useEffect(() => {
    const ref = overflowBoxRef.current;
    ref?.addEventListener('scroll', updateOverflowBox);
    window.addEventListener('resize', updateOverflowBox);
    // Table / flex layout can settle after useLayoutEffect; re-read after paint.
    setTimeout(updateOverflowBox);

    return () => {
      ref?.removeEventListener('scroll', updateOverflowBox);
      window.removeEventListener('resize', updateOverflowBox);
    };
  }, [overflowBoxRef, updateOverflowBox]);

  return (
    <ShadowContainer
      {...restProps}
      depth={depth}
      overflowBox={overflowBox}
      stickyContentBox={stickyContentBox}
    >
      <Shadow
        shadowRegion="topMiddle"
        shadowPlacement={['insideLeft', 'insideRight']}
        borderStyle="solid"
        borderColor="separator.divider"
        borderTopWidth="0px"
        borderBottomWidth="0px"
        borderLeftWidth={
          enableStickyBorders && stickyElementRefs?.left ? '1px' : '0px'
        }
        borderRightWidth={
          enableStickyBorders && stickyElementRefs?.right ? '1px' : '0px'
        }
      />
      <Shadow
        shadowRegion="inside"
        shadowPlacement={[
          'insideTop',
          'insideRight',
          'insideBottom',
          'insideLeft',
        ]}
        borderStyle="solid"
        borderColor="separator.divider"
        borderTopWidth="0px"
        borderBottomWidth="0px"
        borderLeftWidth={
          enableStickyBorders && stickyElementRefs?.left ? '1px' : '0px'
        }
        borderRightWidth={
          enableStickyBorders && stickyElementRefs?.right ? '1px' : '0px'
        }
      />
      <Shadow
        shadowRegion="middleLeft"
        shadowPlacement={['insideTop', 'insideBottom']}
      />
      <Shadow
        shadowRegion="middleRight"
        shadowPlacement={['insideTop', 'insideBottom']}
      />
      <Shadow
        shadowRegion="bottomMiddle"
        shadowPlacement={['insideLeft', 'insideRight']}
        borderStyle="solid"
        borderColor="separator.divider"
        borderTopWidth="0px"
        borderBottomWidth="0px"
        borderLeftWidth={
          enableStickyBorders && stickyElementRefs?.left ? '1px' : '0px'
        }
        borderRightWidth={
          enableStickyBorders && stickyElementRefs?.right ? '1px' : '0px'
        }
      />
    </ShadowContainer>
  );
};
